// Module Import
//---------------------------------
import React, { useState, useCallback, useRef, useMemo, useEffect, useContext } from 'react'
import { useStaticQuery, graphql, navigate } from 'gatsby'
import { Link } from 'gatsby-link'
import useDetectScroll from '@smakss/react-scroll-direction'
import { AppContext } from '../../../context/app-context'
import './NewNavigation.scss'
import useStore from '../../../context/StoreContext'
import { useToast } from '../../../context/ToastContext'
import { PARENT_PAGE_MAP, ROUTES, SUB_PAGE_MAP } from '../../../constants/routes'
// import { searchClient } from '../../../utils/search/search'
import useClickOutside from '../../../hooks/useClickOutside'
import ArrowIcon from '../../../assets/images/icon_arrowOrange.png'
import { getSearchLink } from '../../../helpers/getSearchLink'
import { formatPhoneNumber } from '../../../helpers/formatPhoneNumber'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useAppWrapped } from '../../../context/AppWrappedContext'
import BlackFridayBanner from '../BlackFridayBanner/BlackFridayBanner'
import MobileSubMenu from '../GalleryCarousel/MobileSubMenu'
import collections from '../../../constants/collections'
import MegaMenu from './MegaMenu/MegaMenu'
import CheveronIcon from '../../atoms/icons/CheveronIcon'
import AltCherevonIcon from '../../atoms/icons/AltCheveronIcon'
// import CurrencySwitcher from './CurrencySwitcher/CurrencySwitcher'
import { StaticImage } from 'gatsby-plugin-image'
import { liteClient } from 'algoliasearch/lite'
import { insertElementsOnPosition } from '../../../helpers/insertElementsOnPosition'

// Component Import
//---------------------------------

// Mobile Navigation Styles
//---------------------------------

// Debounce Function
//---------------------------------
function debounce(func, delay) {
  let timeoutId
  return function (...args) {
    if (timeoutId) clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

const searchClient = liteClient(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY
)

// Component Export
//---------------------------------
function GlobalNavigation({ pageContext }) {
  const data = useStaticQuery(graphql`
    query {
      allContentfulPageSubLevel(sort: { createdAt: ASC }) {
        edges {
          node {
            pageSlug
            parentCategory
            navigationTitle
          }
        }
      }
      allContentfulPageRichSubPage(sort: { createdAt: ASC }) {
        edges {
          node {
            pageSlug
            parentCategory
            navigationTitle
          }
        }
      }
      contentfulComponentConfigData {
        contactsPhone
        topBanner
        topBannerText
        topBannerCtaText
        topBannerCtaLink
      }
      contentfulPageDronesTopLevel {
        collectionsList {
          title
          collectionName
          linkDestination
        }
      }
      allContentfulPageCollections(filter: { collectionFamily: { eq: "Enterprise" } }) {
        nodes {
          collectionsList {
            title
            collectionName
            linkDestination
          }
        }
      }
      allContentfulPageProductFamily(filter: { productFamily: { eq: "Emlid GNSS Receivers" } }) {
        edges {
          node {
            id
            contentful_id
            pageSlug
            parentCategory
            navigationTitle
          }
        }
      }
    }
  `)

  // Detect Scrolling & set status
  const { scrollPosition } = useDetectScroll()
  let hasScrolled
  if (scrollPosition.top > 1) {
    hasScrolled = true
  } else {
    hasScrolled = false
  }

  const currentPage = pageContext?.breadcrumb?.location.split('/').slice(0, 2).join('/')

  const isMegaMenu =
    [`${ROUTES.DRONES}`, `${ROUTES.TRAINING_TOP_LEVEL}`].includes(currentPage) &&
    !pageContext?.breadcrumb?.location.includes('emlid-gnss-receivers')

  const isSubMenu = [
    `${ROUTES.SURVEY_TOP_LEVEL}`,
    `${ROUTES.RENTAL}`,
    `${ROUTES.TRAINING_TOP_LEVEL}`,
    `${ROUTES.REPAIRS}`,
    `${ROUTES.DRONES}`,
  ].includes(currentPage)

  const [sliderRef, setSliderRef] = useState(null)
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false)

  const handleMegaMenuTrigger = () => setIsMegaMenuOpen(!isMegaMenuOpen)

  const handleMenuItemClick = (menuHandler) => {
    if (!isMegaMenu && sliderRef?.slickGoTo) {
      sliderRef.slickGoTo(0)
    }
    if (isMegaMenu) setIsMegaMenuOpen(false)
    menuHandler()
  }

  // Filter Navigation Links
  const surveyPages = data.allContentfulPageRichSubPage.edges.filter(
    (el) => el.node.parentCategory === PARENT_PAGE_MAP.SURVEY
  )

  const surveySubPages = data.allContentfulPageSubLevel.edges.filter(
    (el) => el.node.parentCategory === PARENT_PAGE_MAP.SURVEY
  )
  const repairPages = data.allContentfulPageSubLevel.edges.filter(
    (el) => el.node.parentCategory === PARENT_PAGE_MAP.REPAIRS
  )
  const rentalPages = [
    ...data.allContentfulPageSubLevel.edges.filter(
      (el) => el.node.parentCategory === PARENT_PAGE_MAP.RENTAL
    ),
    ...data.contentfulPageDronesTopLevel.collectionsList.filter(({ linkDestination }) =>
      ['rental-collection'].includes(linkDestination)
    ),
  ]
  const trainingPages = [
    ...data.allContentfulPageSubLevel.edges.filter(
      (el) => el.node.parentCategory === PARENT_PAGE_MAP.TRAINING
    ),
    ...data.allContentfulPageRichSubPage.edges.filter(
      (el) => el.node.parentCategory === PARENT_PAGE_MAP.TRAINING
    ),
  ]

  const enterprisePages =
    data.allContentfulPageCollections.nodes?.[0]?.collectionsList.filter((el) =>
      [
        collections.djIDock,
        collections.djIM30,
        collections.djiM300_M350,
        collections.djiMavicEnterprise,
        collections.djiMatrice4,
      ].includes(el.collectionName)
    ) || []

  const dronesPages = [
    {
      title: 'All Drones',
      linkDestination: ROUTES.DRONES,
    },
    ...data.contentfulPageDronesTopLevel.collectionsList.filter((el) =>
      [
        collections.consumerDrones,
        collections.enterpriseDrones,
        collections.proDrones,
        collections.payloads,
        collections.software,
      ].includes(el.collectionName)
    ),
    ...enterprisePages,
  ]

  const phone = data?.contentfulComponentConfigData?.contactsPhone || ''
  const isTopBanner = data?.contentfulComponentConfigData?.topBanner
  const topBannerText = data?.contentfulComponentConfigData?.topBannerText || ''
  const topBannerCtaText = data?.contentfulComponentConfigData?.topBannerCtaText || ''
  const topBannerCtaLink = data?.contentfulComponentConfigData?.topBannerCtaLink || ''

  const { lineItemsCount } = useStore()
  const { showToast } = useToast()
  const { currentMarket } = useStore()

  const [searchInput, setSearchInput] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearchPopover, setIsSearchPopover] = useState(false)
  const searchPopoverRef = useRef(null)
  const headerRef = useRef(null)
  const searchInputRef = useRef(null)

  const [isSearchOpened, setIsSearchOpened] = useState(false)

  const handleOpenSearch = () => {
    setIsSearchOpened(true)
  }

  const handleCloseSearch = () => {
    setIsSearchOpened(false)
  }

  const removeBoldTags = (text) => text.replace(/<\/?b>/gi, '')

  const onSearch = useCallback(
    (manualSearch) => {
      if (searchInput.length < 2) {
        showToast('Validation error', 'Please, enter at least 3 characters')
        return
      }

      const params = new URLSearchParams(window.location.search)
      params.set('query', manualSearch || searchInput)
      navigate(`${ROUTES.SEARCH}?${params.toString()}`)
      handleCloseSearch()
    },
    [searchInput, handleCloseSearch]
  )

  const appWrapperContext = useAppWrapped()

  const currentSubPages = useMemo(() => {
    appWrapperContext?.setShowAdditionalMenu(false)
    if (pageContext.breadcrumb) {
      const isSurvey =
        surveyPages.some((page) => pageContext.breadcrumb.location.includes(page.node.pageSlug)) ||
        pageContext.breadcrumb.location.includes(ROUTES.SURVEY_TOP_LEVEL)
      if (isSurvey) {
        return {
          primary: insertElementsOnPosition({
            parentArray: surveyPages,
            insertableValues: data.allContentfulPageProductFamily.edges,
            position: 3,
          })
            .filter((page) => page.node.pageSlug !== 'gnss-receivers')
            .map((page) => ({
              link: `${SUB_PAGE_MAP[page.node.parentCategory]}/${page.node.pageSlug}`,
              title: page.node.navigationTitle,
              enabled: pageContext.breadcrumb.location.includes(page.node.pageSlug),
            })),
        }
      }

      // check repair routes
      const isRepair =
        repairPages.some((page) => pageContext.breadcrumb.location.includes(page.node.pageSlug)) ||
        pageContext.breadcrumb.location.includes(ROUTES.REPAIRS)
      if (isRepair) {
        return {
          primary: repairPages.map((page) => ({
            link: `${ROUTES.REPAIRS}/${page.node.pageSlug}`,
            title: page.node.navigationTitle,
            enabled: pageContext.breadcrumb.location.includes(page.node.pageSlug),
          })),
        }
      }

      // check rental routes
      const isRental =
        rentalPages.some((page) =>
          pageContext.breadcrumb.location.includes(page.node?.pageSlug || page.linkDestination)
        ) || pageContext.breadcrumb.location.includes(ROUTES.RENTAL)
      if (isRental) {
        const altTitles = {
          Rental: 'View Rental Drones',
        }

        return {
          primary: rentalPages.map((page) => {
            const title = page.node?.navigationTitle || page.title
            return {
              link: page.node
                ? `${ROUTES.RENTAL}/${page.node?.pageSlug}`
                : `${ROUTES.DRONES}/${page?.linkDestination}`,
              title: altTitles[title] || title,
              enabled: pageContext.breadcrumb.location.includes(
                page.node?.pageSlug || page.linkDestination
              ),
            }
          }),
        }
      }

      // check training routes
      // const isTraining =
      //   trainingPages.some((page) =>
      //     pageContext.breadcrumb.location.includes(page.node.pageSlug)
      //   ) || pageContext.breadcrumb.location.includes(ROUTES.TRAINING_TOP_LEVEL)
      // if (isTraining) {
      //   return {
      //     primary: trainingPages.map((page) => ({
      //       link: `${ROUTES.TRAINING_TOP_LEVEL}/${page.node.pageSlug}`,
      //       title: page.node.navigationTitle,
      //       enabled: pageContext.breadcrumb.location.includes(page.node.pageSlug),
      //     })),
      //   }
      // }

      // const isDrones =
      //   dronesPages.some((page) =>
      //     pageContext.breadcrumb.location.includes(page.linkDestination)
      //   ) || pageContext.breadcrumb.location.includes(ROUTES.DRONES)
      // if (isDrones) {
      // USE IF SECONDARY DRONES MENU IS NEEDED
      // let secondary = []
      // const enterpriseLink = dronesPages.find(
      //   (page) => page.linkDestination === 'enterprise-collection'
      // )?.linkDestination

      // const isEnterprise =
      //   pageContext.breadcrumb.location.includes(enterpriseLink) ||
      //   enterprisePages.some((page) =>
      //     pageContext.breadcrumb.location.includes(page.linkDestination)
      //   )
      // if (isEnterprise) {
      //   secondary = enterprisePages.map((page) => ({
      //     link: `${ROUTES.DRONES}/${page.linkDestination}`,
      //     title: page.title,
      //     enabled: pageContext.breadcrumb.location.includes(page.linkDestination),
      //   }))
      //   appWrapperContext?.setShowAdditionalMenu(true)
      // }

      // const altTitles = {
      //   'Cameras, Payloads & Handheld Scanners': 'Payloads and Cameras',
      //   'DJI Mavic Enterprise': 'DJI Mavic 3 Enterprise',
      // }

      // return {
      //   primary: dronesPages.map((page) => ({
      //     link:
      //       page.title === 'All Drones'
      //         ? page.linkDestination
      //         : `${ROUTES.DRONES}/${page.linkDestination}`,
      //     title: altTitles[page.title] || page.title,
      //     enabled:
      //       page.title === 'All Drones'
      //         ? pageContext.breadcrumb.location === `${ROUTES.DRONES}/`
      //         : pageContext.breadcrumb.location.includes(page.linkDestination),
      // USE IF SECONDARY DRONES MENU IS NEEDED
      // ----------------------------------------
      // ||
      // (secondary.length && page.linkDestination.includes(enterpriseLink)),
      // -----------------------------------------
      // })),
      // ------------------------------------------
      // secondary,
      // ------------------------------------------
      // }
      // }
    }

    return { primary: [], secondary: [] }
  }, [
    pageContext.breadcrumb?.location,
    surveyPages,
    surveySubPages,
    repairPages,
    rentalPages,
    trainingPages,
  ])

  const fetchResults = async (input) => {
    if (input.length < 2) {
      return
    }

    const filters = `markets:${currentMarket} OR markets:ALL`
    const hitsPerPage = 10

    const resultsArray = await searchClient.search([
      {
        indexName: 'Products',
        query: input,
        params: {
          hitsPerPage,
          filters,
          getRankingInfo: true,
          attributesToRetrieve: ['objectID', 'title', 'slug'],
          attributesToHighlight: ['title'],
          highlightPreTag: '<mark>',
          highlightPostTag: '</mark>',
        },
      },
      // {
      //   indexName: 'Articles',
      //   query: input,
      //   params: {
      //     hitsPerPage,
      //     filters,
      //     getRankingInfo: true,
      //     attributesToRetrieve: ['objectID', 'title', 'slug', 'contentType'],
      //     attributesToHighlight: ['title'],
      //     highlightPreTag: '<mark>',
      //     highlightPostTag: '</mark>',
      //   },
      // },
      // {
      //   indexName: 'Content',
      //   query: input,
      //   params: {
      //     hitsPerPage,
      //     filters,
      //     getRankingInfo: true,
      //     attributesToRetrieve: ['objectID', 'title', 'slug', 'contentType', 'parentCategory'],
      //     attributesToHighlight: ['title'],
      //     highlightPreTag: '<mark>',
      //     highlightPostTag: '</mark>',
      //   },
      // },
    ])

    const searchTerm = input.toLowerCase()
    
    const combinedHits = [
      ...resultsArray.results[0].hits.map((hit) => ({
        id: hit.objectID,
        _rankingInfo: hit._rankingInfo,
        title: hit?._highlightResult?.title?.value || hit.title,
        slug: hit.slug,
        contentType: 'product',
        originalTitle: hit.title,
      })),
      // ...resultsArray.results[1].hits.map((hit) => ({
      //   id: hit.objectID,
      //   _rankingInfo: hit._rankingInfo,
      //   title: hit?._highlightResult?.title?.value || hit.title,
      //   slug: hit.slug,
      //   contentType: hit.contentType,
      // })),
      // ...resultsArray.results[2].hits.map((hit) => ({
      //   id: hit.objectID,
      //   _rankingInfo: hit._rankingInfo,
      //   title: hit?._highlightResult?.title?.value || hit.title,
      //   slug: hit.slug,
      //   contentType: hit.contentType,
      //   parentCategory: hit?.parentCategory,
      // })),
    ]
      .sort((a, b) => {
        const titleA = (a.originalTitle || a.title || '').toLowerCase()
        const titleB = (b.originalTitle || b.title || '').toLowerCase()
        const slugA = (a.slug || '').toLowerCase()
        const slugB = (b.slug || '').toLowerCase()
        
        const matchesTitleA = titleA.includes(searchTerm)
        const matchesTitleB = titleB.includes(searchTerm)
        const matchesSlugA = slugA.includes(searchTerm)
        const matchesSlugB = slugB.includes(searchTerm)
        
        if (matchesTitleA && !matchesTitleB) return -1
        if (!matchesTitleA && matchesTitleB) return 1
        if (matchesSlugA && !matchesSlugB) return -1
        if (!matchesSlugA && matchesSlugB) return 1
        
        const typosA = a._rankingInfo?.nbTypos ?? Infinity
        const typosB = b._rankingInfo?.nbTypos ?? Infinity
        if (typosA !== typosB) return typosA - typosB
        
        const firstWordA = a._rankingInfo?.firstMatchedWord ?? Infinity
        const firstWordB = b._rankingInfo?.firstMatchedWord ?? Infinity
        if (firstWordA !== firstWordB) return firstWordA - firstWordB
        
        const wordsA = a._rankingInfo?.words ?? 0
        const wordsB = b._rankingInfo?.words ?? 0
        if (wordsA !== wordsB) return wordsB - wordsA
        
        const scoreA = a._rankingInfo?.userScore ?? 0
        const scoreB = b._rankingInfo?.userScore ?? 0
        return scoreB - scoreA
      })
      .slice(0, 20)

    setSearchResults(combinedHits)
    setIsSearchPopover(true)
    // try {
    //   const contentfulFetch = async (query) => {
    //     const response = await fetch(
    //       `https://cdn.contentful.com/spaces/et769tc4wc1v/environments/${process.env.GATSBY_CONTENTFUL_BRANCH ?? 'development'}/entries?access_token=${process.env.GATSBY_CONTENTFUL_CDA_TOKEN}&query=${query}&limit=50`
    //     )
    //     return response.json()
    //   }
    //
    //   const [search, predictiveSearch, contentful] = await Promise.all([
    //     searchClient.searchTitles(input, 50),
    //     searchClient.predictiveSearchTitles(input, 50),
    //     contentfulFetch(input),
    //   ])
    //
    //   const allProducts = [
    //     ...search.edges,
    //     ...(predictiveSearch?.products.map((product) => ({ node: product })) || []),
    //   ]
    //
    //   const uniqueProducts = allProducts.filter(
    //     (product, index, self) =>
    //       index === self.findIndex((p) => p.node.id === product.node.id) &&
    //       product.node.featuredImage &&
    //       !product.node.tags.includes('Hidden')
    //   )
    //
    //   const filteredContentful = contentful.items.filter((item) =>
    //     [
    //       'pageHomapage',
    //       'pageCollections',
    //       'pageBlogArticle',
    //       'pageSupportGuide',
    //       'pageSubLevelTemplate',
    //       'pageTrainingTopLevel',
    //       'pageProductFamily',
    //       'pageSurveyTopLevel',
    //       'pageRichSubPage',
    //       'pageBasicPage',
    //       'pageIndustry',
    //       'pageIndustrySubLevel',
    //       'pageGsdCalculator',
    //       'pageTrainingCalculator',
    //       'pageJob',
    //       'pageRepairCalculator',
    //       'pageTermsAndConditions',
    //     ].includes(item.sys.contentType.sys.id)
    //   )
    //
    //   const combinedResults = [
    //     ...uniqueProducts.map((product) => ({
    //       title: product.node.title,
    //       contentType: 'product',
    //       slug: product.node.handle,
    //     })),
    //     ...filteredContentful.map((item) => ({
    //       title:
    //         item.title ||
    //         item.fields.articleTitle ||
    //         item.fields.mastheadTitle ||
    //         item.fields.mastheadJobTitle ||
    //         item.fields.duration ||
    //         item.fields.department ||
    //         item.fields.mastheadTitleLine,
    //       contentType: item.sys.contentType.sys.id,
    //       slug: item.fields.pageSlug,
    //       parentCategory: item.fields.parentCategory || '',
    //     })),
    //   ]
    //
    //   const exactResults = []
    //   const restResults = []
    //
    //   combinedResults.forEach((result) => {
    //     if (result.title.toLowerCase().includes(input.toLowerCase())) {
    //       exactResults.push(result)
    //     } else {
    //       restResults.push(result)
    //     }
    //   })
    //
    //   const sortedResults = [...exactResults, ...restResults]
    //
    //   setSearchResults(sortedResults)
    //   setIsSearchPopover(true)
    // } catch (error) {
    //   console.error('Error fetching search results:', error)
    //   showToast('Error', 'Failed to fetch search results')
    // }
  }

  const debouncedFetchResults = useRef(debounce(fetchResults, 500)).current

  const handleInputChange = useCallback(
    (e) => {
      setSearchInput(e.target.value)
      debouncedFetchResults(e.target.value)

      if (e.target.value.length < 2) setIsSearchPopover(false)
    },
    [debouncedFetchResults]
  )

  const highlightMatches = (result, query) => {
    const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const escapedQuery = query.split(' ').map(escapeRegExp).join('|')
    const regex = new RegExp(`(${escapedQuery})`, 'gi')
    return result.replace(regex, (match) => `<b>${match}</b>`)
  }

  const appContext = useContext(AppContext)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflowY = isMegaMenuOpen ? 'hidden' : 'auto'
      document.body.style.height = isMegaMenuOpen ? '100dvh' : 'auto'
    } else {
      document.body.style.overflowY = 'auto'
      document.body.style.height = 'auto'
    }
  }, [isMegaMenuOpen, isMobile])

  const highlightedResults = useMemo(
    () =>
      searchResults.map((result) => ({
        ...result,
        // title: highlightMatches(result.title, searchInput),
      })),
    [searchResults, searchInput]
  )

  useClickOutside(searchPopoverRef, () => setIsSearchPopover(false))
  useClickOutside(headerRef, () => appContext?.CloseAll())

  // add focus to the search field
  useEffect(() => {
    if (isSearchOpened && searchInputRef?.current) {
      searchInputRef?.current.focus()
    }
  }, [isSearchOpened])

  useEffect(() => {
    document.body.classList.toggle('black-friday', Boolean(isTopBanner))
  }, [isTopBanner])

  return (
    <AppContext.Consumer>
      {({
        globalNavActive,
        CloseAll,
        ToggleGlobalNav,
        isSubMenuActive,
        closeSubMenu,
        openSubMenu,
        closeFirstMenu,
      }) => (
        <header
          ref={headerRef}
          className={`GlobalNavWrapper ${
            hasScrolled ? '' : 'GlobalNavWrapper--notScrolled'
          } ${globalNavActive ? '' : 'GlobalNavWrapper--menuClosed'} ${isMegaMenuOpen && 'mega-menu-opened'} ${isTopBanner && 'black-friday'}`}
        >
          <nav className="NavigationWrapper">
            {isTopBanner && (
              <BlackFridayBanner
                bannerText={topBannerText}
                ctaText={topBannerCtaText}
                ctaLink={topBannerCtaLink}
              />
            )}
            <div className="outer">
              <div className={`inner ${isSearchOpened ? 'input-opened' : ''}`}>
                <Link className="LogoWrapper" to="/" aria-label="main page">
                  <StaticImage
                    src="../../../assets/images/heliguy_logo.svg"
                    alt="Heliguy logo"
                    loading="eager"
                    width={160}
                    height={82}
                  />
                </Link>
                <div className="SearchInputWrapper">
                  <input
                    ref={searchInputRef}
                    onKeyDown={(e) => {
                      if (e.code === 'Enter') onSearch()
                    }}
                    placeholder="Search heliguy™"
                    value={searchInput}
                    onChange={handleInputChange}
                    className="SearchInput"
                    type="search"
                  />
                  <button onClick={() => onSearch()} className="SearchButton" type="submit" />
                  <button onClick={handleCloseSearch} className="CloseButton" />
                  {isSearchPopover && (
                    <div ref={searchPopoverRef} className="SearchPopover">
                      {highlightedResults.map((item) => (
                        <button
                          onClick={() => {
                            setIsSearchPopover(false)
                            setSearchInput('')
                            setIsSearchOpened(false)
                            navigate(
                              getSearchLink(item.contentType, item.slug, item.parentCategory)
                            )
                          }}
                          className="SearchPopoverItem"
                          dangerouslySetInnerHTML={{ __html: item.title }}
                        />
                      ))}
                      <button
                        onClick={() => {
                          setIsSearchPopover(false)

                          onSearch(searchInput)
                        }}
                        className="SearchPopoverItem SearchPopoverBtn"
                      >
                        <span>Search for "{searchInput}"</span>
                        <span>
                          <img alt="arrow" src={ArrowIcon} />
                        </span>
                      </button>
                    </div>
                  )}
                </div>
                <ul className="SecondaryNavLinkList">
                  <li className="SecondaryNavLinkListItem">
                    <button
                      onClick={handleOpenSearch}
                      className="SearchButtonMobileToggle SearchButton"
                      aria-label="search"
                    >
                      <span className="FakeInput">Search</span>
                      <span className="NavigationButton--search"></span>
                    </button>
                  </li>
                  {/* <li className="SecondaryNavLinkListItem currency-switcher-item">
                    <CurrencySwitcher />
                  </li> */}
                  <li className="SecondaryNavLinkListItem">
                    <Link
                      to={ROUTES.CART}
                      className="NavigationButton NavigationButton--cart"
                      aria-label="shopping cart"
                      onClick={CloseAll}
                    >
                      {lineItemsCount ? (
                        <span className="CartItemCount">{lineItemsCount}</span>
                      ) : (
                        ''
                      )}
                    </Link>
                  </li>
                  <li className="SecondaryNavLinkListItem">
                    <Link
                      to={ROUTES.ACCOUNT}
                      className="NavigationButton NavigationButton--login"
                      onClick={CloseAll}
                      aria-label="login"
                    />
                  </li>
                  <li className="SecondaryNavLinkListItem">
                    <a
                      href="https://training.heliguy.com/"
                      className="NavigationButton NavigationButton--school"
                      target="_blank"
                      rel="noreferrer"
                      aria-label="training"
                    />
                  </li>
                  <li className="SecondaryNavLinkListItem">
                    <a
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      className="NavigationButton NavigationButton--phone"
                      aria-label="contact phone"
                    />
                  </li>
                  <li className="SecondaryNavLinkListItem">
                    <a
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      className="NavigationPhoneLink"
                      aria-label="contact phone"
                    >
                      {formatPhoneNumber(phone)}
                    </a>
                  </li>
                </ul>
                <div className="MobileMenuButtonWrapper">
                  <button
                    type="button"
                    className="MobileMenuButton"
                    onClick={() => {
                      ToggleGlobalNav(isSubMenu, isMegaMenu)
                      setIsMegaMenuOpen(false)
                    }}
                    aria-label="toggle menu"
                  />
                </div>
                {/* <div className="currency-switcher">
                  <CurrencySwitcher />
                </div> */}
                <div className={`MainNavDrawer ${globalNavActive ? 'show' : 'hidden'}`}>
                  <div className="MainNavFlex">
                    <ul className="MainNavLinkList MainNavFirst">
                      <li
                        className={`MainNavLinkListItem ${
                          currentPage === ROUTES.DRONES &&
                          !pageContext?.breadcrumb?.location.includes('emlid-gnss-receivers')
                            ? ' with-trigger'
                            : ''
                        }`}
                      >
                        <Link to={ROUTES.DRONES} onClick={() => handleMenuItemClick(openSubMenu)}>
                          Drones
                        </Link>
                        {currentPage === ROUTES.DRONES &&
                          !pageContext?.breadcrumb?.location.includes('emlid-gnss-receivers') && (
                            <button
                              className={`mega-menu-trigger ${isMegaMenuOpen ? ' mega-menu-trigger-close' : ''}${
                                isMobile ? ' mobile-trigger' : ''
                              }`}
                              type="button"
                              onClick={() => {
                                openSubMenu()
                                handleMegaMenuTrigger()
                              }}
                            >
                              {isMobile ? (
                                <AltCherevonIcon size={40} />
                              ) : (
                                <CheveronIcon size={16} />
                              )}
                            </button>
                          )}
                      </li>
                      <li
                        className={`MainNavLinkListItem ${currentPage === ROUTES.TRAINING_TOP_LEVEL ? ' with-trigger' : ''}`}
                      >
                        <Link
                          to={ROUTES.TRAINING_TOP_LEVEL}
                          onClick={() => handleMenuItemClick(openSubMenu)}
                        >
                          Training
                        </Link>
                        {currentPage === ROUTES.TRAINING_TOP_LEVEL && (
                          <button
                            className={`mega-menu-trigger ${isMegaMenuOpen ? ' mega-menu-trigger-close' : ''}${isMobile ? ' mobile-trigger' : ''}`}
                            type="button"
                            onClick={handleMegaMenuTrigger}
                          >
                            {isMobile ? <AltCherevonIcon size={40} /> : <CheveronIcon size={16} />}
                          </button>
                        )}
                      </li>
                      <li className="MainNavLinkListItem">
                        <Link
                          to={ROUTES.RENTAL}
                          onClick={() => handleMenuItemClick(closeFirstMenu)}
                        >
                          Rental
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem">
                        <Link
                          to={ROUTES.SURVEY_TOP_LEVEL}
                          onClick={() => handleMenuItemClick(closeFirstMenu)}
                        >
                          Survey
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem">
                        <Link
                          to="/dji-drones-shop/handheld-3d-laser-scanners/"
                          onClick={() => handleMenuItemClick(closeFirstMenu)}
                        >
                          XGRIDS
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem">
                        <Link
                          to={ROUTES.REPAIRS}
                          onClick={() => handleMenuItemClick(closeFirstMenu)}
                        >
                          Repairs
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem">
                        <Link to={ROUTES.BLOG} onClick={closeSubMenu}>
                          Blog
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem non-clickable-label">|</li>
                    </ul>
                    <ul className="MainNavLinkList MainNavSecond" aria-label="secondary">
                      <li className="MainNavLinkListItem special-item">
                        <Link to={ROUTES.UTILITIES} onClick={closeSubMenu}>
                          Utilities
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem special-item">
                        <Link to={ROUTES.FIRST_RESPONDER_PAGE} onClick={closeSubMenu}>
                          DFR
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem special-item">
                        <Link to={ROUTES.DRONES_SECURITY_PAGE} onClick={closeSubMenu}>
                          Security
                        </Link>
                      </li>
                      <li className="MainNavLinkListItem special-item">
                        <Link to={ROUTES.REALITY_CAPTURE_PAGE} onClick={closeSubMenu}>
                          Reality capture
                        </Link>
                      </li>
                    </ul>
                  </div>
                  {/* <div className="currency-switcher">
                    <CurrencySwitcher />
                  </div> */}
                </div>
              </div>
            </div>
          </nav>

          {isMobile ? (
            <nav
              className={`MegaMenuWrapper ${isSubMenuActive && ((!!currentSubPages?.primary?.length && !pageContext?.breadcrumb?.location.includes('rental-collection')) || (isMegaMenu && isMegaMenuOpen)) ? `show ${isMegaMenu ? '' : 'with-next-arrow'}` : 'hidden'}`}
            >
              {isMegaMenu && !pageContext?.breadcrumb?.location.includes('emlid-gnss-receivers') ? (
                <MegaMenu
                  isOpen={isMegaMenuOpen}
                  onToggleMegaMenu={setIsMegaMenuOpen}
                  mobile
                  currentPage={currentPage}
                  isGlobalNavActive={globalNavActive}
                  onClickItem={CloseAll}
                />
              ) : (
                <div className="outer sub-menu-wrapper">
                  <div className="inner">
                    <div className="SubNavList">
                      <MobileSubMenu setSliderRef={setSliderRef}>
                        {!!currentSubPages?.primary?.length &&
                          currentSubPages?.primary?.map((target, index) => (
                            <div
                              className={`SubNavListItem ${target.enabled ? 'enabled' : ''}`}
                              key={index}
                            >
                              <Link to={target.link}>{target.title}</Link>
                            </div>
                          ))}
                      </MobileSubMenu>
                    </div>
                  </div>
                </div>
              )}
            </nav>
          ) : (
            <nav
              className={`MegaMenuWrapper ${(currentSubPages?.primary?.length && !isMegaMenu) || (isMegaMenu && isMegaMenuOpen) ? 'show' : 'hidden'}`}
            >
              {isMegaMenu ? (
                <MegaMenu
                  isOpen={isMegaMenuOpen}
                  currentPage={currentPage}
                  onToggleMegaMenu={setIsMegaMenuOpen}
                />
              ) : (
                <div className="outer">
                  <div className="inner">
                    <ul className="SubNavList">
                      {!!currentSubPages?.primary?.length &&
                        currentSubPages?.primary?.map((target, index) => (
                          <li
                            className={`SubNavListItem ${target.enabled ? 'enabled' : ''}`}
                            key={index}
                          >
                            <Link to={target.link}>{target.title}</Link>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              )}
            </nav>
          )}

          <nav
            className={`MegaMenuWrapper ${(isMobile ? globalNavActive && !!currentSubPages?.secondary?.length : !!currentSubPages?.secondary?.length) && appWrapperContext?.showAdditionalMenu ? 'show' : 'hidden'}`}
          >
            {/* ADD SECONDURY SUB MENU */}
            {/* <div className="outer">
              <div className="inner">
                <ul className="SubNavList">
                  {!!currentSubPages?.secondary?.length &&
                    currentSubPages?.secondary?.map((target, index) => (
                      <li
                        className={`SubNavListItem ${target.enabled ? 'enabled' : ''}`}
                        key={index}
                      >
                        <Link to={target.link} onClick={CloseAll}>
                          {target.title}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
            </div> */}
          </nav>
        </header>
      )}
    </AppContext.Consumer>
  )
}

export default GlobalNavigation

GlobalNavigation.propTypes = {}
